<script lang="ts">
	import CourseWaitlistCta from '$lib/CourseWaitlistCta.svelte';
	import DividendCalculator from '$lib/DividendCalculator.svelte';
	import DividendPayoutRatioChart from '$lib/charts/DividendPayoutRatioChart.svelte';
	import DividendPerShareChart from '$lib/charts/DividendPerShareChart.svelte';
	import DividendYieldChart from '$lib/charts/DividendYieldChart.svelte';
	import { valueComparisonAdjective } from '$lib/utils';
	import { consolidatedDividends, getTtmDividendYield } from '$lib/utils/dividends';
	import dayjs from 'dayjs';
	import * as ss from 'simple-statistics';
	import type { PageData } from './$types';
	import DividendPayoutHistory from './DividendPayoutHistory.svelte';
	import DividendSection from './DividendSection.svelte';
	import PriceTargetEstimator from './PriceTargetEstimator.svelte';

	interface Props {
		data: PageData;
	}

	let { data }: Props = $props();
	let {
		symbol,
		quote,
		etfQuote,
		reitQuote,
		dividendPayoutHistory,
		dividendFyPayoutHistory,
		dividendPayoutRatioHistory,
		dividendYieldHistory,
		dividends
	} = $derived(data);
	let { regularMarketPrice } = $derived(etfQuote ?? reitQuote ?? quote!);

	let forwardDividendRate = $derived(reitQuote?.dividendRate ?? quote?.dividendRate ?? undefined);
	let forwardDividendYield = $derived(
		reitQuote?.dividendYield ?? quote?.dividendYield ?? undefined
	);
</script>

<section class="tab-mb element-visibility" data-type="dividend-section" data-content="overview">
	<h2 data-toc="true" class="h2 my-6">Overview</h2>
	<DividendSection
		{symbol}
		{dividends}
		{dividendYieldHistory}
		{forwardDividendYield}
		dividendPayout={dividendFyPayoutHistory?.find(({ forecast }) => !forecast)}
		dividendPayoutRatio={dividendPayoutRatioHistory?.at(-1)}
		ttmDividendYield={getTtmDividendYield(regularMarketPrice, dividends)}
	/>
</section>

<section
	class="tab-mb element-visibility max-w-screen-sm"
	data-type="dividend-section"
	data-content="course-signup-module"
>
	<CourseWaitlistCta />
</section>

{#if dividendFyPayoutHistory?.length}
	{@const actualDividendFyPayoutHistory = dividendFyPayoutHistory.filter(
		({ forecast }) => !forecast
	)}
	<section
		class="tab-mb element-visibility max-w-screen-sm"
		data-type="dividend-section"
		data-content="dividend-per-share"
	>
		<h2 data-toc="true" class="h2 mb-3">Dividend per share</h2>
		{#if actualDividendFyPayoutHistory.length > 1}
			<p class="mb-6">
				{symbol.name}'s dividend per share of {actualDividendFyPayoutHistory[0].total.toLocaleString(
					'default',
					{
						style: 'currency',
						currency: symbol.currency,
						currencyDisplay: 'narrowSymbol',
						minimumFractionDigits: 2,
						maximumFractionDigits: 6
					}
				)} in {actualDividendFyPayoutHistory[0].year} was {valueComparisonAdjective(
					actualDividendFyPayoutHistory[0].total,
					actualDividendFyPayoutHistory[1].total
				)} its dividend per share of {actualDividendFyPayoutHistory[1].total.toLocaleString(
					'default',
					{
						style: 'currency',
						currency: symbol.currency,
						currencyDisplay: 'narrowSymbol',
						minimumFractionDigits: 2,
						maximumFractionDigits: 6
					}
				)} in {actualDividendFyPayoutHistory[1].year}.
				{#if forwardDividendRate}
					According to consensus forecasts, {symbol.name} is expected to pay a dividend per share of
					{forwardDividendRate.toLocaleString('default', {
						style: 'currency',
						currency: symbol.currency,
						currencyDisplay: 'narrowSymbol',
						minimumFractionDigits: 2,
						maximumFractionDigits: 6
					})} in {Number(actualDividendFyPayoutHistory[0].year) + 1}, {valueComparisonAdjective(
						forwardDividendRate,
						actualDividendFyPayoutHistory[0].total
					)} its actual dividend per share in {actualDividendFyPayoutHistory[0].year}.
				{/if}
			</p>
		{/if}
		<div class="chart-container mb-5">
			<DividendPerShareChart
				dividendPayoutHistory={[...dividendFyPayoutHistory].slice(0, 6).reverse()}
				currency={symbol.currency}
			/>
		</div>

		<figure class="border-gray-faint mb-5 rounded-md border-1 border-solid p-2">
			<table class="[&_td]:index-regular [&_th]:index-bold w-full [&_td]:text-center">
				<thead>
					<tr>
						<th>Fiscal Year</th>
						<th>Dividend Per Share</th>
						<th>Dividend Yield</th>
					</tr>
				</thead>
				<tbody>
					{#each dividendFyPayoutHistory.slice(0, 6) as { year, total, dividendYield, forecast }}
						<tr>
							<td
								>{year}{#if forecast}&nbsp;(Forecast){/if}</td
							>
							<td
								>{total.toLocaleString('default', {
									style: 'currency',
									currency: symbol.currency,
									currencyDisplay: 'narrowSymbol',
									minimumFractionDigits: 2,
									maximumFractionDigits: 6
								})}</td
							>
							<td
								>{dividendYield?.toLocaleString('default', {
									style: 'percent',
									minimumFractionDigits: 1,
									maximumFractionDigits: 1
								})}</td
							>
						</tr>
					{/each}
				</tbody>
			</table>
		</figure>
		<p class="body-bold">
			Explore dividend investing by comparing the
			<a href="/best-online-brokerage-singapore" class="hyperlink"
				>the Best online brokerage & trading platform in Singapore [2025]
			</a> today!
		</p>
	</section>
{/if}

{#if dividends?.length && forwardDividendRate}
	<section
		id="calculator"
		class="tab-mb element-visibility max-w-screen-sm scroll-mt-48"
		data-type="dividend-section"
		data-content="dividend-payment-calculator"
	>
		<h2 data-toc="true" class="h2 mb-3">Dividend payment calculator</h2>
		<p class="mb-6">
			Find out how much dividends you will potentially receive based on the number of {symbol.name} shares
			held
		</p>
		<div class="card-container mb-5">
			<DividendCalculator
				name={symbol.name}
				currentPrice={regularMarketPrice}
				dividendRate={forwardDividendRate}
				currency={symbol.currency}
			/>
		</div>
	</section>
{/if}

{#if dividendYieldHistory?.length}
	{@const currentDividendYield =
		dividendYieldHistory[dividendYieldHistory.length - 1].dividendYield}
	{@const meanDividendYield = ss.mean(
		dividendYieldHistory.map(({ dividendYield }) => dividendYield)
	)}
	{@const dividendYieldSd = ss.standardDeviation(
		dividendYieldHistory.map(({ dividendYield }) => dividendYield)
	)}
	<section
		class="tab-mb element-visibility max-w-screen-sm"
		data-type="dividend-section"
		data-content="dividend-yield"
	>
		<h2 data-toc="true" class="h2 mb-6">Dividend yield</h2>
		<div class="mb-5 grid max-w-md grid-flow-col gap-3">
			<div>
				<span class="index-regular">Current Dividend Yield</span><br />
				<span class="body-bold"
					>{isNaN(currentDividendYield)
						? 'N/A'
						: currentDividendYield?.toLocaleString('default', {
								style: 'percent',
								maximumFractionDigits: 1
							})}</span
				>
			</div>
			<div>
				<span class="index-regular">Historical Average Dividend Yield</span><br />
				<span class="body-bold">
					{isNaN(meanDividendYield)
						? 'N/A'
						: meanDividendYield.toLocaleString('default', {
								style: 'percent',
								maximumFractionDigits: 1
							})}</span
				>
			</div>
			{#if reitQuote?.sectorYield}
				<div>
					<span class="index-regular">Sector Average Dividend Yield</span><br />
					<span class="body-bold"
						>{reitQuote.sectorYield.toLocaleString('default', {
							style: 'percent',
							maximumFractionDigits: 1
						})}</span
					>
				</div>
			{/if}
		</div>
		{#if isFinite(currentDividendYield)}
			<p>
				{symbol.name}'s current dividend yield of {currentDividendYield.toLocaleString('default', {
					style: 'percent',
					minimumFractionDigits: 1,
					maximumFractionDigits: 1
				})} is {valueComparisonAdjective(currentDividendYield, meanDividendYield)}
				its historical average of {meanDividendYield.toLocaleString('default', {
					style: 'percent',
					minimumFractionDigits: 1,
					maximumFractionDigits: 1
				})}.
			</p>
			{#if currentDividendYield > meanDividendYield + dividendYieldSd}
				<p class="mt-3">
					{symbol.name}'s current dividend yield is more than 1 standard deviation above its
					historical average dividend yield.
				</p>
			{:else if currentDividendYield < meanDividendYield - dividendYieldSd}
				<p class="mt-3">
					{symbol.name}'s current dividend yield is less than 1 standard deviation below its
					historical average dividend yield.
				</p>
			{/if}
			{#if currentDividendYield > 0.04}
				<p class="mt-3">
					<a href="/best-singapore-high-dividend-stocks" class="hyperlink"
						>View stocks with dividend yield of above 4%</a
					>
				</p>
			{/if}
		{/if}

		<div class="line-chart-container my-5">
			<DividendYieldChart
				data={dividendYieldHistory}
				mean={meanDividendYield}
				sd={dividendYieldSd}
			/>
		</div>
	</section>

	{#if forwardDividendRate}
		<h2 class="h3 mb-3">Price Target Estimator</h2>
		<section
			class="section-mb element-visibility max-w-screen-sm"
			data-type="dividend-section"
			data-content="price-target-estimator"
		>
			<p class="mb-6">
				Calculate the estimated yield you would receive based on your target entry price or exit
				price of {symbol.name}.
			</p>
			<PriceTargetEstimator
				currency={symbol.currency}
				{dividendYieldHistory}
				dividendRate={forwardDividendRate}
				sectorAverageYield={reitQuote?.sectorYield}
			/>
		</section>
	{/if}
{/if}

<h2 data-toc="true" class="h2 mb-3">Dividend payout history</h2>
<section
	class="section-mb element-visibility max-w-screen-sm"
	data-type="dividend-section"
	data-content="dividend-payout-history"
>
	{#if dividendPayoutHistory?.length}
		{@const lastDividendPayout = consolidatedDividends(dividends).find(({ paymentDate }) =>
			dayjs(paymentDate).isBefore(dayjs())
		)}
		<p class="mb-6">
			{#if symbol.dividendFrequency}
				{symbol.name} historically paid
				<span class="font-medium">dividends {symbol.dividendFrequency.toLowerCase()}</span>.
			{/if}
			{#if lastDividendPayout}
				It last paid out a dividend per share of {lastDividendPayout.declaredRate.toLocaleString(
					'default',
					{
						style: 'currency',
						currency: symbol.currency,
						currencyDisplay: 'narrowSymbol',
						minimumFractionDigits: 2,
						maximumFractionDigits: 6
					}
				)} with an ex-dividend date of {dayjs(lastDividendPayout.exDate).format('DD MMM YYYY')} and payment
				date of {dayjs(lastDividendPayout.paymentDate).format('DD MMM YYYY')}.
			{/if}
		</p>

		<DividendPayoutHistory {dividendPayoutHistory} currency={symbol.currency} />
		<p class="small-bold text-gray-dark mt-3 mb-3 text-right">
			Source: SGX as of {dayjs(symbol.sgxSecurity.lastCrawled).format('DD MMM YYYY')}
		</p>
	{:else}
		<p class="body-regular text-gray-dark mt-6 text-center lg:text-left">
			There is no dividend data is currently available for {symbol.name}.
		</p>
	{/if}
</section>

{#if dividendPayoutRatioHistory?.length}
	{@const lastDividendPayoutRatio = dividendPayoutRatioHistory.slice(-1)[0]}
	{@const prevDividendPayoutRatio = dividendPayoutRatioHistory.slice(-2)[0]}
	<section
		class="element-visibility max-w-screen-sm"
		data-type="dividend-section"
		data-content="dividend-payout-ratio"
	>
		<h2 data-toc="true" class="h2 mb-3">Dividend payout ratio</h2>
		<p class="mb-6">
			{symbol.name}'s payout ratio in {lastDividendPayoutRatio.year} was {lastDividendPayoutRatio.payoutRatio.toLocaleString(
				'default',
				{
					style: 'percent'
				}
			)}, {valueComparisonAdjective(
				lastDividendPayoutRatio.payoutRatio,
				prevDividendPayoutRatio.payoutRatio
			)} its payout ratio of {prevDividendPayoutRatio.payoutRatio.toLocaleString('default', {
				style: 'percent'
			})} in {prevDividendPayoutRatio.year}.
		</p>
		<div class="chart-container mb-5">
			<DividendPayoutRatioChart {dividendPayoutRatioHistory} />
		</div>

		<figure class="border-gray-faint rounded-md border-1 border-solid p-2">
			<table class="[&_td]:index-regular [&_th]:index-bold w-full [&_td]:text-center">
				<thead>
					<tr>
						<th>Fiscal Year</th>
						<th>Dividend Payout ratio</th>
					</tr>
				</thead>
				<tbody>
					{#each [...dividendPayoutRatioHistory].reverse() as { year, payoutRatio }}
						<tr>
							<td>{year}</td>
							<td
								>{payoutRatio.toLocaleString('default', {
									style: 'percent'
								})}</td
							>
						</tr>
					{/each}
				</tbody>
			</table>
		</figure>
	</section>
{/if}
